import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  FormGroup,
  Input,
  Button,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import NF from "../../../assets/images/file.png";
import { createSelector } from "reselect";
import Skeleton from "react-loading-skeleton";
import {
  addtaxMaster,
  deleteTaxMaster,
  fetchTaxList,
  updateTaxMaster,
  updateTaxStatus,
} from "../../../slices/TaxMaster/thunk";
import CustomPagination from "../../../Components/Common/CustomePagination";
import AddTaxModal from "../../../Modals/TaxMaster/AddTaxMasterModal";
import ConfirmModal from "../../../Components/Common/ConfirmModal";
import EditTaxModal from "../../../Modals/TaxMaster/EditTaxModal";

const TaxMaster = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedTaxId, setSelectedTaxId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTax, setSelectedTax] = useState(null);

  const selectTaxListState = (state) => state;
  const taxListSelector = createSelector(selectTaxListState, (state) => ({
    taxes: state.TaxMaster.taxes,
    loading: state.TaxMaster.loading,
    error: state.TaxMaster.error,
    pagination: state.TaxMaster.pagination,
  }));

  const { taxes, loading, error, pagination } = useSelector(taxListSelector);

  const handleAddTax = async (queryParam) => {
    await dispatch(addtaxMaster(queryParam));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(fetchTaxList(params));
  };

  const handleDeleteClick = (taxId) => {
    setSelectedTaxId(taxId);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    await dispatch(deleteTaxMaster(selectedTaxId));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(fetchTaxList(params));
  };

  const handleEdit = async (updatedTax) => {
    await dispatch(updateTaxMaster(updatedTax));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(fetchTaxList(params));
  };

  const handleEditClick = (tax) => {
    setSelectedTax(tax);
    setIsEditModalOpen(true);
  };

  const handleToggleStatus = async (taxId, currentStatus) => {
    const newStatus = {
      taxAction: !currentStatus,
    };
    await dispatch(updateTaxStatus(taxId, newStatus));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(fetchTaxList(params));
  };

  useEffect(() => {
    const delay = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  useEffect(() => {
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
      searchTerm: debouncedSearch.trim(),
    };
    dispatch(fetchTaxList(params));
  }, [dispatch, pageNo, pageSize, debouncedSearch]);

  document.title = "Tax List";

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <h4 className="card-title mb-0">Tax List</h4>
              </CardHeader>

              <CardBody className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <div className="search-box" style={{ maxWidth: "300px" }}>
                    <input
                      type="text"
                      className="form-control search"
                      placeholder="Search tax..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setPageNo(0);
                      }}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>

                  <Button
                    color="primary"
                    className="btn btn-sm"
                    onClick={() => setIsAddModalOpen(true)}
                  >
                    <i className="ri-add-line align-bottom me-1"></i>
                    Add Tax
                  </Button>
                </div>

                <div className="flex-grow-1 overflow-auto">
                  <table className="table align-middle table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Sr. No</th>
                        <th>Tax Name</th>
                        <th>Tax Percentage (%)</th>
                        {/* <th>Status</th> */}

                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading &&
                        [...Array(pageSize)].map((_, index) => (
                          <tr key={index}>
                            <td>
                              <Skeleton height={15} width={40} />
                            </td>
                            <td>
                              <Skeleton height={15} width={100} />
                            </td>
                            <td>
                              <Skeleton height={15} width={60} />
                            </td>
                            <td>
                              <Skeleton height={15} width={150} />
                            </td>
                            <td>
                              <Skeleton height={15} width={50} />
                            </td>
                          </tr>
                        ))}

                      {!loading && error && (
                        <tr>
                          <td colSpan="5" className="text-center text-danger">
                            {error}
                          </td>
                        </tr>
                      )}

                      {!loading && !error && taxes?.length === 0 && (
                        <tr>
                          <td colSpan="5" className="text-center text-danger">
                            <img
                              src={NF}
                              alt="No File"
                              style={{ width: "500px", marginTop: "10px" }}
                            />
                          </td>
                        </tr>
                      )}

                      {!loading &&
                        taxes?.map((tax, index) => (
                          <tr key={tax?.id || index}>
                            <td>{pageNo * pageSize + index + 1}</td>
                            <td>{tax?.name || "-"}</td>
                            <td>{tax?.taxPerc || "-"}</td>
                            {/* <td onClick={(e) => e.stopPropagation()}>
                              <FormGroup switch>
                                <Input
                                  type="switch"
                                  id={`userActiveSwitch-${tax?.id}`}
                                  checked={tax?.active}
                                  onChange={() =>
                                    handleToggleStatus(tax?.id, tax?.active)
                                  }
                                  className={
                                    tax?.active
                                      ? "form-check-input bg-primary border-success"
                                      : ""
                                  }
                                />
                              </FormGroup>
                            </td> */}
                            <td>
                              <div className="d-flex gap-2">
                                <button
                                  type="button"
                                  class="btn btn-sm btn-outline-success btn-icon waves-effect waves-light"
                                  onClick={() => handleEditClick(tax)}
                                >
                                  <i class="ri-edit-2-line"></i>
                                </button>

                                <button
                                  type="button"
                                  class="btn btn-sm btn-outline-danger btn-icon waves-effect waves-light"
                                  onClick={() => handleDeleteClick(tax?.id)}
                                >
                                  <i class="ri-delete-bin-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3" style={{ minHeight: "50px" }}>
                  <CustomPagination
                    currentPage={pagination?.currentPage}
                    totalPages={pagination?.totalPages}
                    onPageChange={(page) => setPageNo(page)}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    setPageNo={setPageNo}
                  />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {isAddModalOpen && (
        <AddTaxModal
          isOpen={isAddModalOpen}
          toggle={() => setIsAddModalOpen(!isAddModalOpen)}
          onSubmit={handleAddTax}
        />
      )}

      {isConfirmModalOpen && (
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          toggle={() => setIsConfirmModalOpen(false)}
          title="Confirm Deletion"
          message="Are you sure you want to delete this tax?"
          onConfirm={handleConfirmDelete}
        />
      )}
      {isEditModalOpen && (
        <EditTaxModal
          isOpen={isEditModalOpen}
          toggle={() => setIsEditModalOpen(false)}
          taxData={selectedTax}
          onSubmit={handleEdit}
        />
      )}
    </div>
  );
};

export default TaxMaster;
